import React, { FC, useContext } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router';
import styles from './Header.module.scss';
import { LogoLight } from 'esa-ui-library';
import { User, Dropdown } from 'esa-ui-library';
import { v4 as uuid } from 'uuid';
import {
	userPages,
	mainNavPages,
	mainNavRestricted,
	PropertyType,
	superAdminNavPages,
	fullFinancialNavigation,
	fullPlayersNavigation,
} from '../../../common';
import { AuthContext } from '../../../logic';
import { NavItem } from './NavItem';
import { PERMISSIONS } from '../../constants';

interface HeaderProps {
	dataId?: string;
}

const Header: FC<HeaderProps> = ({ dataId }): JSX.Element => {
	const { user, logout } = useContext(AuthContext);
	const history = useHistory();
	const location = useLocation();

	const isItemActive = (val: string) => location.pathname === val;

	const onNavItemClick = (i: PropertyType) => {
		history.push(i.value);
	};

	const onUserItemClick = (i: PropertyType) => {
		if (i.value === 'logout') return logout();
		onNavItemClick({ ...i, value: `${i.value}/${user?.id}` });
	};

	const getActiveItem = (items: PropertyType[]): PropertyType | undefined => {
		return items.find((i) => i.value === location.pathname);
	};

	const getOptionTestId = (label: string) => `${label.replace(/ /g, '-').toLowerCase()}-option`;

	const getDropdownClass = (data: PropertyType[]) =>
		`${styles.NavDropdown} ${getActiveItem(data) ? styles.Active : ''}`;

	const renderNavItem = (i: PropertyType) => (
		<span
			key={uuid()}
			className={`${styles.NavItem} ${isItemActive(i.value) ? styles.Active : ''}`}
			onClick={() => onNavItemClick(i)}
			data-testid={getOptionTestId(i.label)}
		>
			{i.label}
		</span>
	);

	const renderNavDropdown = (data: PropertyType) => (
		<NavItem
			key={uuid()}
			data={data}
			getOptionTestId={getOptionTestId}
			getActiveItem={getActiveItem}
			onNavItemClick={onNavItemClick}
		/>
	);

	const renderUserDropdown = () => {
		if (user) {
			return (
				<Dropdown
					label={user.username}
					options={userPages}
					onChange={onUserItemClick}
					selected={getActiveItem(userPages)}
					right
					dataId='user-dropdown'
					className={getDropdownClass(userPages)}
				/>
			);
		}
	};

	const hasRole = (role: string) => user?.roles?.some((r: any) => r.value === role);

	const renderMainNav = () => {
		const canListGames = user?.permissions?.[PERMISSIONS.canListGames];
		const canListCampaigns = user?.permissions?.[PERMISSIONS.canListCampaigns];

		if (user?.permissions?.isSuperAdmin) {
			return superAdminNavPages.map((p: PropertyType) => (p.options ? renderNavDropdown(p) : renderNavItem(p)));
		}

		let pages: PropertyType[] = [];

		if (hasRole('FullFinancial') && hasRole('FullPlayers')) {
			pages = [...mainNavRestricted];
		} else if (hasRole('FullFinancial')) {
			pages = [...fullFinancialNavigation];
		} else if (hasRole('FullPlayers')) {
			pages = [...fullPlayersNavigation];
		} else {
			pages = user?.permissions?.canViewUserDetails ? [...mainNavPages] : [...mainNavRestricted];
		}

		if (canListGames) {
			const gamesPage = superAdminNavPages.find((p) => p.label === 'Games');
			if (gamesPage && !pages.some((p) => p.label === 'Games')) pages.push(gamesPage);
		}

		if (canListCampaigns) {
			const promotionalToolsPage = superAdminNavPages.find((p) => p.label === 'Promotional Tools');
			if (promotionalToolsPage && !pages.some((p) => p.label === 'Promotional Tools')) pages.push(promotionalToolsPage);
		}

		return pages.map((p: PropertyType) => (p.options ? renderNavDropdown(p) : renderNavItem(p)));
	};

	return (
		<header className={styles.Header} data-testid={dataId}>
			<section className={styles.NavContainer}>
				<LogoLight className={styles.Logo} />
				<section className={styles.MainNav}>{renderMainNav()}</section>
			</section>
			<section className={styles.User}>
				{user?.image && <img className={styles.UserImage} src={user.image} alt='' />}
				{!user?.image && <User className={styles.UserIcon} />}
				{renderUserDropdown()}
			</section>
		</header>
	);
};

Header.propTypes = {
	dataId: PropTypes.string,
};

export { Header };
